import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Globe2, Menu, X } from "lucide-react";

/**
 * Marketing site shell — shared header, footer and bilingual context.
 * Used by every /heavenly/* page.
 */

// ---------- bilingual context ----------
const MarketingI18nContext = createContext({ lang: "en", setLang: () => {} });

const STORAGE_KEY = "heavenly_marketing_lang";

export const MarketingI18nProvider = ({ children }) => {
    const [lang, setLangState] = useState("en");
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved === "en" || saved === "es") setLangState(saved);
            else {
                const nav = (navigator.language || "en").toLowerCase();
                if (nav.startsWith("es")) setLangState("es");
            }
        } catch (err) {
            // localStorage may be blocked (private browsing); fall through to in-memory state
            console.debug("[marketing-i18n] read failed:", err);
        }
    }, []);
    const setLang = (l) => {
        setLangState(l);
        try { localStorage.setItem(STORAGE_KEY, l); } catch (err) {
            console.debug("[marketing-i18n] write failed:", err);
        }
    };
    return (
        <MarketingI18nContext.Provider value={{ lang, setLang }}>
            {children}
        </MarketingI18nContext.Provider>
    );
};

export const useMLang = () => useContext(MarketingI18nContext);

// ---------- copy dictionaries ----------
const NAV = {
    en: {
        home: "Home",
        features: "Features",
        pricing: "Pricing",
        blog: "Blog",
        docs: "Docs",
        demo: "See the demo",
        cta: "Start free trial",
        rights: "All rights reserved.",
        builtBy: "Built by HeavenlyAIsolutions",
        contact: "Contact",
        tagline: "A beautiful restaurant site in five minutes. Orders straight to WhatsApp.",
    },
    es: {
        home: "Inicio",
        features: "Funciones",
        pricing: "Precios",
        blog: "Blog",
        docs: "Documentación",
        demo: "Ver el demo",
        cta: "Empieza tu prueba gratis",
        rights: "Todos los derechos reservados.",
        builtBy: "Hecho por HeavenlyAIsolutions",
        contact: "Contacto",
        tagline: "Un sitio para restaurantes hermoso en cinco minutos. Pedidos directos por WhatsApp.",
    },
};

export const useMCopy = () => {
    const { lang } = useMLang();
    return { lang, t: NAV[lang] };
};

// ---------- shared layout ----------
const Header = () => {
    const { lang, setLang } = useMLang();
    const t = NAV[lang];
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    useEffect(() => setOpen(false), [loc.pathname]);

    const navCls = ({ isActive }) =>
        `text-sm transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`;

    return (
        <header className="relative z-30 px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between bg-[#0f0f12]/80 backdrop-blur border-b border-white/5 sticky top-0">
            <Link to="/heavenly" className="flex items-center gap-2.5" data-testid="marketing-logo">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center font-serif font-black text-white">H</div>
                <div>
                    <div className="font-serif font-bold text-lg leading-none text-white">HeavenlyEats</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">by HeavenlyAIsolutions</div>
                </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
                <NavLink to="/heavenly" end className={navCls} data-testid="nav-home">{t.home}</NavLink>
                <NavLink to="/heavenly/features" className={navCls} data-testid="nav-features">{t.features}</NavLink>
                <NavLink to="/heavenly/pricing" className={navCls} data-testid="nav-pricing">{t.pricing}</NavLink>
                <NavLink to="/heavenly/blog" className={navCls} data-testid="nav-blog">{t.blog}</NavLink>
                <NavLink to="/heavenly/docs" className={navCls} data-testid="nav-docs">{t.docs}</NavLink>
            </nav>

            <div className="flex items-center gap-3">
                <button
                    onClick={() => setLang(lang === "en" ? "es" : "en")}
                    data-testid="marketing-lang-toggle"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 text-xs text-white/80 hover:text-white"
                >
                    <Globe2 className="w-3.5 h-3.5" />
                    {lang === "en" ? "EN · ES" : "ES · EN"}
                </button>
                <Link
                    to="/r/delicias"
                    data-testid="marketing-header-demo-btn"
                    className="hidden md:inline-flex px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-white/90"
                >
                    {t.demo}
                </Link>
                <button
                    className="lg:hidden p-2 text-white"
                    onClick={() => setOpen(!open)}
                    data-testid="marketing-mobile-menu-btn"
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="absolute top-full left-0 right-0 bg-[#0f0f12] border-b border-white/10 lg:hidden">
                    <nav className="flex flex-col px-6 py-4 gap-1">
                        <NavLink to="/heavenly" end className="py-2 text-white/80">{t.home}</NavLink>
                        <NavLink to="/heavenly/features" className="py-2 text-white/80">{t.features}</NavLink>
                        <NavLink to="/heavenly/pricing" className="py-2 text-white/80">{t.pricing}</NavLink>
                        <NavLink to="/heavenly/blog" className="py-2 text-white/80">{t.blog}</NavLink>
                        <NavLink to="/heavenly/docs" className="py-2 text-white/80">{t.docs}</NavLink>
                        <button
                            onClick={() => setLang(lang === "en" ? "es" : "en")}
                            className="mt-2 text-left py-2 text-white/60"
                        >
                            <Globe2 className="w-3.5 h-3.5 inline mr-2" />
                            {lang === "en" ? "Cambiar a Español" : "Switch to English"}
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
};

const Footer = () => {
    const { lang } = useMLang();
    const t = NAV[lang];
    return (
        <footer className="relative z-10 mt-20 border-t border-white/10 px-6 sm:px-10 lg:px-16 py-10 text-white/50">
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5 text-sm">
                <div>© {new Date().getFullYear()} HeavenlyEats · {t.builtBy}</div>
                <div className="flex gap-5">
                    <Link to="/heavenly/pricing" className="hover:text-white">{t.pricing}</Link>
                    <Link to="/heavenly/docs" className="hover:text-white">{t.docs}</Link>
                    <Link to="/r/delicias" className="hover:text-white">{t.demo}</Link>
                    <a href="mailto:hello@heavenlyai.solutions" className="hover:text-white">{t.contact}</a>
                </div>
            </div>
        </footer>
    );
};

const MarketingLayout = ({ children, testid = "marketing-page" }) => (
    <div data-testid={testid} className="min-h-screen bg-[#0f0f12] text-white relative overflow-hidden">
        {/* Background gradient + grain */}
        <div className="fixed inset-0 z-0 opacity-50 pointer-events-none"
             style={{ background: "radial-gradient(ellipse at top left, #C85A1733 0%, transparent 60%), radial-gradient(ellipse at bottom right, #4A7C5933 0%, transparent 60%)" }} />
        <div className="fixed inset-0 z-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
             style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
        <Header />
        <main className="relative z-10">{children}</main>
        <Footer />
    </div>
);

export default MarketingLayout;
